// Pricing resolver (P4-WP11c) — the ONE place that answers "what price does this
// customer pay for this product, at this quantity, in this unit?". Reads the WP11a/b/c
// pricing tables and composes them; NOTHING else in the app implements pricing logic.
//
// TIER ORDER (highest → lowest specificity), first tier with an APPLICABLE row wins:
//   (a) customer_prices   (b) customer's own price_list   (c) customer's group's list
//   (d) company default price_list (is_default, active)   (e) base products.wholesale_price
// If customerId is null: skip a/b/c → resolve via (d) then (e).
//
// SET-BASED: a fixed 6 queries for the WHOLE basket (no N+1). Fetch once, resolve all
// lines in memory. Never throws for "no price" — tier (e) base is the floor (0 if the
// product has no wholesale_price). It DOES throw if any of the 6 queries itself fails —
// a resolver that cannot resolve must fail loudly, never invent a number. Silently
// falling through to a later tier on a denied/failed read would produce a fabricated
// price shape-indistinguishable from a real one (e.g. a genuine €0.00 base floor).
//
// UNITS: a base price is stored canonically as unit_id NULL. A unit-specific price is
// "per one of that unit"; product_units.factor = base units per one of the unit.

import { supabase } from "./supabaseClient";

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}

// Pick the applicable row with the HIGHEST min_qty that is still ≤ threshold.
export function bestByMinQty(rows, threshold) {
  let best = null;
  for (const r of rows) if (r.min_qty <= threshold && (!best || r.min_qty > best.min_qty)) best = r;
  return best;
}

/**
 * Resolve one tier's rows for a product into a price, applying the unit + qty-break rule.
 * @returns {{ unitPrice:number, matchedMinQty:number }|null}  null = tier does not apply.
 */
export function resolveInTier(rows, reqUnitId, qty, F) {
  // Exact-unit rows (both null = base, or both the same unit id).
  const exact = rows.filter((r) => (r.unit_id || null) === (reqUnitId || null));
  const exactBest = bestByMinQty(exact, qty);
  if (exactBest) return { unitPrice: Number(exactBest.unit_price), matchedMinQty: exactBest.min_qty };
  // Else derive from base rows (unit_id NULL): compare against the qty expressed in base units.
  if (reqUnitId) {
    const baseRows = rows.filter((r) => !r.unit_id);
    const baseBest = bestByMinQty(baseRows, qty * F);
    if (baseBest) return { unitPrice: Number(baseBest.unit_price) * F, matchedMinQty: baseBest.min_qty };
  }
  return null;
}

/**
 * Batch engine. lines = [{ productId, qty, unitId? }]. Returns one result per line:
 *   { productId, unitPrice, source, sourceLabel, matchedMinQty|null, unit:{id,code,factor}|'base', currency }
 */
export async function resolvePrices(customerId, lines = []) {
  const productIds = [...new Set(lines.map((l) => l.productId).filter(Boolean))];
  if (productIds.length === 0) return lines.map((l) => baseFloor(l.productId, 0, null, "base"));

  const c = client();

  // q1 — the customer + their group's list (skip if no customer).
  let ownListId = null, groupListId = null;
  if (customerId) {
    const { data, error } = await c.from("customers")
      .select("price_list_id, customer_group_id, customer_groups ( price_list_id )")
      .eq("id", customerId).maybeSingle();
    if (error) throw error;
    ownListId = data?.price_list_id || null;
    groupListId = data?.customer_groups?.price_list_id || null;
  }

  // q2 — the relevant lists (own/group) + the company default, ACTIVE ONLY (refinement D:
  // an archived list a customer is still linked to must not apply — it falls through).
  const relevantIds = [...new Set([ownListId, groupListId].filter(Boolean))];
  let listQ = c.from("price_lists").select("id, name, currency, status, is_default").eq("status", "active");
  listQ = relevantIds.length ? listQ.or(`id.in.(${relevantIds.join(",")}),is_default.eq.true`) : listQ.eq("is_default", true);
  const { data: listRows, error: listErr } = await listQ;
  if (listErr) throw listErr;
  const activeLists = listRows || [];
  const listById = Object.fromEntries(activeLists.map((l) => [l.id, l]));
  const ownList = ownListId ? listById[ownListId] : null;                 // null if archived/missing
  const groupList = groupListId ? listById[groupListId] : null;
  const defaultList = activeLists.find((l) => l.is_default) || null;
  const activeListIds = activeLists.map((l) => l.id);

  // q3 — customer_prices for this customer + basket (skip if no customer).
  let cpRows = [];
  if (customerId) {
    const { data, error } = await c.from("customer_prices").select("product_id, unit_id, min_qty, unit_price")
      .eq("customer_id", customerId).in("product_id", productIds);
    if (error) throw error;
    cpRows = data || [];
  }
  // q4 — price_list_items for the active lists + basket.
  let liRows = [];
  if (activeListIds.length) {
    const { data, error } = await c.from("price_list_items").select("price_list_id, product_id, unit_id, min_qty, unit_price")
      .in("price_list_id", activeListIds).in("product_id", productIds);
    if (error) throw error;
    liRows = data || [];
  }
  // q5 — product_units (factors + base unit).
  const { data: unitRows, error: unitErr } = await c.from("product_units").select("id, product_id, code, factor, is_base").in("product_id", productIds);
  if (unitErr) throw unitErr;
  // q6 — base prices (self-contained: the resolver fetches its own floor).
  const { data: prodRows, error: prodErr } = await c.from("products").select("id, wholesale_price").in("id", productIds);
  if (prodErr) throw prodErr;

  // ── Index everything for in-memory resolution ──────────────────────────────
  const cpByProduct = groupBy(cpRows || [], "product_id");
  const unitsByProduct = groupBy(unitRows || [], "product_id");
  const baseByProduct = Object.fromEntries((prodRows || []).map((p) => [p.id, p.wholesale_price]));
  const liByListProduct = {};   // listId → productId → rows
  for (const r of liRows) {
    (liByListProduct[r.price_list_id] || (liByListProduct[r.price_list_id] = {}));
    (liByListProduct[r.price_list_id][r.product_id] || (liByListProduct[r.price_list_id][r.product_id] = [])).push(r);
  }
  const listItemsFor = (list, productId) => (list && liByListProduct[list.id] && liByListProduct[list.id][productId]) || [];

  // ── Resolve each line ──────────────────────────────────────────────────────
  return lines.map((line) => {
    const productId = line.productId;
    const qty = Number(line.qty) || 1;
    if (!productId) return baseFloor(productId, 0, null, "base");

    // Determine the requested unit U. Normalise the product's base unit to the null/base
    // case (refinement B — base is never represented two ways).
    const units = unitsByProduct[productId] || [];
    const u = line.unitId ? units.find((x) => x.id === line.unitId) : null;
    const isUnit = !!(u && !u.is_base);                     // a real non-base pack unit
    const reqUnitId = isUnit ? u.id : null;                 // null = base
    const F = isUnit ? Number(u.factor) : 1;
    const unitOut = isUnit ? { id: u.id, code: u.code, factor: F } : "base";

    // Try each tier in specificity order; first applicable wins.
    const tiers = [];
    if (customerId) tiers.push({ rows: cpByProduct[productId] || [], source: "customer", label: "Customer price", currency: "EUR" });
    if (customerId && ownList) tiers.push({ rows: listItemsFor(ownList, productId), source: "customer_list", label: ownList.name, currency: ownList.currency });
    if (customerId && groupList) tiers.push({ rows: listItemsFor(groupList, productId), source: "group_list", label: groupList.name, currency: groupList.currency });
    if (defaultList) tiers.push({ rows: listItemsFor(defaultList, productId), source: "default_list", label: defaultList.name, currency: defaultList.currency });

    for (const t of tiers) {
      const hit = resolveInTier(t.rows, reqUnitId, qty, F);
      if (hit) return { productId, unitPrice: round2(hit.unitPrice), source: t.source, sourceLabel: t.label, matchedMinQty: hit.matchedMinQty, unit: unitOut, currency: t.currency || "EUR" };
    }
    // (e) base floor — wholesale_price × F (per requested unit).
    return baseFloor(productId, round2((Number(baseByProduct[productId]) || 0) * F), null, unitOut);
  });
}

/** Convenience wrapper — resolve a single line. */
export async function resolvePrice(customerId, productId, qty = 1, unitId) {
  const [r] = await resolvePrices(customerId, [{ productId, qty, unitId }]);
  return r;
}

// ── helpers ────────────────────────────────────────────────────────────────────
function baseFloor(productId, unitPrice, matchedMinQty, unit) {
  return { productId, unitPrice: round2(unitPrice), source: "base", sourceLabel: "Base price", matchedMinQty, unit: unit || "base", currency: "EUR" };
}
function groupBy(rows, key) {
  const out = {};
  for (const r of rows) (out[r[key]] || (out[r[key]] = [])).push(r);
  return out;
}
